

import { Component, Vue, Prop } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

// @ts-ignore
import cloneDeep from "lodash/cloneDeep"

import { Team } from "@/types"
import User, { SystemUser } from "@/types/user"
import { Subscription } from "@/types/subscription"

import api from "@/services/api"
import systemApi from "@/services/api/api_System"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "@/components/Config/DataTable.vue"
import UserImage from "@/components/UserImage.vue"
import apiTenant from "@/services/api_Tenant"

const setup = namespace("setup")

@Component({
	components: {
		"skeleton": SkeletonLoader,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"user-image": UserImage
	}
})
export default class SystemUsers extends Vue {
	@State public systemUser!: SystemUser

	@setup.Action public getADEnabled!: () => Promise<boolean>

	public filter: string = "";

	public users: SystemUser[] = [];
	private editUser: SystemUser | null = null;

	public loading: boolean = true;
	public adEnabled: boolean = false;

	private errorEmailValue: string | null = null;
	private emailStatus: { [key: string]: boolean } = {};

	/**
	 * On mount, get users page.
	 */
	private async mounted (): Promise<void> {
		this.loading = true

		await this.reloadData()
		this.getADEnabled().then(enabled => this.adEnabled = enabled)

		this.loading = false
	}

	/**
	 * List of columns for a table
	 */
	public get columns () {
		return [
			{
				key: "image",
				hasTemplate: true,
				inModal: () => false
			},

			{
				key: "name",
				label: "Name",
				sortable: true,
				errors: (item: any) => item.name ? null : "Please enter a name"
			},

			{
				key: "email",
				label: "Email",
				sortable: true,
				readonly: this.editUser && this.editUser.id,
				errors: (item: any) => {
					if (this.editUser &&
						this.editUser.id &&
						this.editUser.email === item.email
					) {
						return null
					}

					if (!item.email) return "Please enter an email address"

					const regx = /\S+@\S+\.\S+/
					if (!regx.test(String(item.email).toLocaleLowerCase())) {
						return "Please enter a valid email address"
					}

					if (this.emailStatus[item.email]) {
						return "This email address is already in use"
					}

					api.emailExists(item.email).then(exists => {
						this.emailStatus[item.email] = exists
						if (exists) this.errorEmailValue = item.email
					})

					return null
				},
				status: this.emailStatus,
				errorValue: this.errorEmailValue
			},

			{
				key: "pictureUrl",
				label: "Picture",
				inTable: false,
				tooltip: "Provide a URL to a location where the image is stored for this user."
			},

			{
				key: "supportAccess",
				label: "Support Session",
				inTable: true,
				hasTemplate: true,
				inModal: () => false
			},

			{
				key: "adAccount",
				label: "Active Directory Account",
				inTable: false,
				inModal: (item: any) => this.adEnabled,
				readonly: this.editUser && this.editUser.id
			}
		]
	}

	/**
	 * Loads a list of users for further display in a table
	 */
	private async loadUsers (): Promise<void> {
		this.users = await systemApi.systemUsers()
	}

	/**
	 * Reloads data for users as well as subscription
	 * after creating or deleting a user
	 */
	private async reloadData (): Promise<void> {
		try {
			await Promise.all([
				this.loadUsers()
			])
		} catch (err) {
			this.$bvToast.toast(err as any, {
				title: "Error reload data",
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}
	}

	/**
	 * Loads full information about the selected user for further editing
	 */
	public async loadUserDetails (item: User): Promise<User> {
		const itemClone = cloneDeep(item)

		this.editUser = itemClone
		this.emailStatus = {}

		return itemClone
	}

	/**
	 * Create and update a user
	 */
	public async saveItem (user: SystemUser): Promise<void> {
		try {
			await systemApi.systemUserSave(user)
		} catch (err) {
			this.$bvToast.toast(err as any, {
				title: `Error saving user: ${user.name}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}

		this.editUser = null

		await this.reloadData()
	}

	/**
	 * Remove a user
	 */
	public async deleteItem (user: SystemUser): Promise<void> {
		try {
			await systemApi.systemUserDelete(user)
		} catch (err) {
			this.$bvToast.toast(err as any, {
				title: `Error deleting user: ${user.name}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}

		this.editUser = null
		this.emailStatus = {}

		await this.reloadData()
	}
}

